/**
 * Automation templates and the script-name sets derived from them.
 *
 * Extracted from `automations-section.tsx` when the automations list moved to
 * its own top-level page: the list config needs the same "which automations are
 * hidden" rules the settings section applied, and a list config importing from
 * a settings *component* is both a layering smell and a `react-refresh`
 * violation (a component file that also exports constants breaks fast refresh).
 */

// Type-only: `Plus` names the icon-component shape, it is never rendered here.
import type { Plus } from '@untitledui/icons';
import {
  ArrowCircleBrokenDown,
  BellRinging01,
  Users01,
} from '@untitledui/icons';

export interface AutomationTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: typeof Plus;
  /** Feature flag required to show this template. Undefined = always visible. */
  readonly featureFlag?: string;
  /**
   * Internal automations are hidden from users (add library + active list)
   * unless the `internal_automations` flag is active. They are managed centrally
   * and must not be user-toggleable.
   */
  readonly internal?: boolean;
  /** Script names that identify automations created from this template. */
  readonly scriptNames: readonly string[];
}

export const AUTOMATION_TEMPLATES: readonly AutomationTemplate[] = [
  {
    id: 'lead_rotation',
    name: 'Lead rotation',
    description:
      'Assign new contacts to team members using round-robin rotation.',
    icon: Users01,
    scriptNames: ['lead_rotation'],
  },
  {
    id: 'crm_webhook',
    name: 'CRM ingestion',
    description: 'Receive events from external services into your CRM.',
    icon: ArrowCircleBrokenDown,
    featureFlag: 'crm_webhook',
    // One CRM ingestion automation per org for v1 (ahoy_automation_name is unique).
    scriptNames: ['crm_ingestion'],
  },
  {
    id: 'date_reminders',
    name: 'Date reminders',
    description:
      'Surface upcoming date properties on the calendar as reminders.',
    icon: BellRinging01,
    // Internal: managed centrally (enabled for all orgs) and not user-toggleable.
    // Hidden unless the internal_automations flag is active.
    internal: true,
    scriptNames: ['reminder_from_date_properties'],
  },
];

/** Script names managed by the Websites settings section — excluded here. */
export const WEBSITE_SCRIPT_NAMES = new Set([
  'web_tracking_code',
  'web_form_creation',
  'web_form_submission_processor',
]);

/**
 * Script names belonging to "internal" automation templates — derived from the
 * templates above so every surface that lists automations stays in sync. These
 * are filtered out unless the internal_automations flag is active.
 */
export const INTERNAL_SCRIPT_NAMES = new Set(
  AUTOMATION_TEMPLATES.filter(t => t.internal).flatMap(t => [...t.scriptNames])
);

/**
 * Reveals templates marked `internal`. They are managed centrally and are not
 * user-toggleable, so they stay hidden without it.
 */
const INTERNAL_AUTOMATIONS_FLAG = 'internal_automations';

/**
 * Templates visible to the current org, given its active feature flags: a
 * template is hidden if it names a `featureFlag` the org lacks, or if it is
 * `internal` and the org lacks `internal_automations`.
 */
export function filterAutomationTemplates(
  templates: readonly AutomationTemplate[],
  { activeFlags }: { activeFlags: readonly string[] }
): AutomationTemplate[] {
  return templates.filter(t => {
    if (t.featureFlag && !activeFlags.includes(t.featureFlag)) {
      return false;
    }
    if (t.internal && !activeFlags.includes(INTERNAL_AUTOMATIONS_FLAG)) {
      return false;
    }
    return true;
  });
}
